import { Link } from "react-router-dom";
import { useLang, useT } from "../i18n";
import { breadcrumbLd, faqLd, useSeo, webPageLd } from "../lib/seo";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

function StepList({ title, steps }: { title: string; steps: { title: string; text: string }[] }) {
  return (
    <div>
      <h2 className="m-0 mb-4 text-lg font-medium">{title}</h2>
      <ol className="m-0 list-none border-t border-line-strong p-0">
        {steps.map((step, i) => (
          <li key={step.title} className="flex gap-5 border-b border-line-strong py-3.5">
            <span className="w-4 shrink-0 font-mono text-xs font-medium text-brand">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div>
              <b className="text-[13px] font-medium">{step.title}</b>
              <p className="m-0 mt-0.5 text-xs leading-normal text-muted">{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function HowItWorksPage() {
  const t = useT();
  const page = t.howPage;
  const lang = useLang();
  useSeo({
    title: t.seo.how.title,
    fullTitle: true, // "… – LE COIN VTC" is part of the title
    description: t.seo.how.description,
    path: "/how-it-works",
    jsonLd: [
      webPageLd("WebPage", t.seo.how.title, t.seo.how.description, "/how-it-works", lang),
      // The drivers' steps as a how-to, and the questions as an FAQ
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: page.findVehicle,
        description: page.intro,
        step: page.driverSteps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.text })),
      },
      faqLd(page.faq),
      breadcrumbLd([[t.seo.breadcrumbHome, "/"], [page.title]]),
    ],
  });
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header activeHref="/how-it-works" />

      <main className="mx-auto w-full max-w-300 flex-1 px-4 pt-12 pb-24 sm:px-6">
        <h1 className="m-0 text-2xl sm:text-3xl font-medium tracking-[-0.01em] sm:text-[40px]">{page.title}</h1>
        <p className="mt-2 mb-10 max-w-full text-[15px] leading-normal text-muted">
          {page.intro}
        </p>

        <div className="grid gap-12 md:grid-cols-2 md:gap-9">
          <div>
            <StepList title={page.driversTitle} steps={page.driverSteps} />
            <Link
              to="/vehicles"
              className="mt-6 inline-flex h-10 items-center rounded bg-brand px-4.5 text-[13px] font-medium text-white no-underline hover:bg-brand-dark"
            >
              {page.findVehicle}
            </Link>
          </div>
          <div>
            <StepList title={page.companiesTitle} steps={page.companySteps} />
            <Link
              to="/company"
              className="mt-6 inline-flex h-10 items-center rounded bg-surface px-4.5 text-[13px] font-medium text-ink no-underline hover:bg-surface-hover"
            >
              {page.companyArea}
            </Link>
          </div>
        </div>

        <h2 className="mt-14 mb-3 text-lg font-medium">{page.faqTitle}</h2>
        <div className="border-t border-line-strong">
          {page.faq.map((item) => (
            <details key={item.q} className="group border-b border-line-strong">
              <summary className="flex cursor-pointer list-none items-center gap-2.5 py-3.5 text-[13px] font-medium [&::-webkit-details-marker]:hidden">
                <span className="text-[10px] transition-transform group-open:rotate-90">▶</span>
                {item.q}
              </summary>
              <p className="m-0 pb-4 pl-5 text-[13px] leading-relaxed text-muted">{item.a}</p>
            </details>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
